import { type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "~/lib/utils";
import { useMapLayout } from "./useMapLayout";
import { INTERNAL_REGION_IDS, REGION_IDS } from "./constants";
import { useBreakpoint } from "~/hooks/useBreakpoint";
import { BottomSheet } from "~/components/ui/bottom-sheet";

interface PanelProps {
  children: ReactNode;
  className?: string;
  /** Panel title for accessibility */
  title?: string;
}

const DEFAULT_MOBILE_STEP = "peek" as const;

/**
 * MapLayout.Panel - Side panel on desktop, bottom sheet on mobile.
 */
export default function Panel({ children, className, title }: PanelProps) {
  const { mounted, sheetStep, setSheetStep } = useMapLayout();
  const { below } = useBreakpoint();
  const showMobilePanel = below("lg");
  const desktopRef = useRef<HTMLElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  // Reset scroll when direct children change
  useEffect(() => {
    const scrollContainer = showMobilePanel ? mobileRef.current : desktopRef.current;
    if (!scrollContainer) return;

    const observer = new MutationObserver(() => {
      scrollContainer.scrollTo(0, 0);
    });

    observer.observe(scrollContainer, { childList: true });

    return () => observer.disconnect();
  }, [showMobilePanel]);

  // Set initial step on mobile, clear when switching to desktop or unmounting
  useEffect(() => {
    if (showMobilePanel) {
      setSheetStep(DEFAULT_MOBILE_STEP);
    } else {
      setSheetStep(null);
    }
    return () => setSheetStep(null);
  }, [showMobilePanel, setSheetStep]);

  if (!mounted) return null;

  if (showMobilePanel) {
    const mobileTarget = document.getElementById(INTERNAL_REGION_IDS.panelMobile);
    if (!mobileTarget) return null;

    return createPortal(
      <div className="fixed inset-x-0 bottom-0 z-floating-panel md:max-w-md md:mx-auto">
        <BottomSheet.Root
          defaultStep={DEFAULT_MOBILE_STEP}
          step={sheetStep ?? undefined}
          onStepChange={setSheetStep}
          dismissible={false}
          className={cn("md:shadow-2xl", className)}
        >
          <BottomSheet.Handle />
          {title && (
            <BottomSheet.Header>
              <BottomSheet.Title>{title}</BottomSheet.Title>
            </BottomSheet.Header>
          )}
          <BottomSheet.Body ref={mobileRef}>{children}</BottomSheet.Body>
        </BottomSheet.Root>
      </div>,
      mobileTarget,
    );
  }

  const target = document.getElementById(REGION_IDS.panel);
  if (!target) return null;

  return createPortal(
    <aside
      ref={desktopRef}
      className={cn(
        "h-fit max-h-full overflow-y-auto rounded-lg border border-border bg-background shadow-lg",
        className,
      )}
      aria-label={title}
    >
      {title && <div className="sticky top-0 bg-background px-4 py-3 font-semibold border-b">{title}</div>}
      {children}
    </aside>,
    target,
  );
}
